import { useEffect, useState } from "react";

import CssBaseline from "@mui/material/CssBaseline";
import { Emptyable, IsoLocale, Nullable, UserFormPermissions, UserGeneralPermissions } from "@simplyk/common";
import { useRouter } from "next/router";

import { CurrentUserProvider } from "../../../../common/contexts/CurrentUserContext";
import { EmailEditorProvider } from "../../../../common/contexts/EmailEditorContext";
import { LocaleProvider } from "../../../../common/contexts/LocaleContext";
import { StripeAccountProvider } from "../../../../common/contexts/StripeAccountContext";
import { isAdminRoute, isDonorRoute, isOrganizationRoute } from "../../../../common/helpers/auth";
import { useLogParamsEvent } from "../../../../common/hooks/amplitude/useLogParamsEvent";
import { useDisableSafariBackForwardCache } from "../../../../common/hooks/useDisableSafariBackForwardCache";
import { AmplitudeProvider } from "../../../../common/providers/AmplitudeProvider";
import { OrganizationEngagementFlagsProvider } from "../../../../common/providers/OrganizationEngagementFlagsProvider";
import { PageLoadingProvider } from "../../../../common/providers/PageLoadingProvider";
import { StrongAuthProvider } from "../../../../common/providers/PasswordProvider";
import { TwoFactorProvider } from "../../../../common/providers/TwoFactorProvider";
import { DevContextCopy } from "../../DevContextCopy/DevContextCopy";
import { EmailTypeChoiceProvider } from "../../EmailTypeChoiceDrawer";
import { Mandatory2FASetupModal } from "../../Mandatory2FASetupModal/Mandatory2FASetupModal";
import { OrganizationCategoryPopup } from "../../OrganizationCategoryPopup/OrganizationCategoryPopup";
import { DonorRoute } from "../../Routes/DonorRoute";
import { OrganizationRoute } from "../../Routes/OrganizationRoute";
import { PublicRoute } from "../../Routes/PublicRoute";

import { ClarityProviderWrapper } from "./ClarityProviderWrapper";

import { ZeffySnackbarProvider } from "@/dashboard-design-system/Snackbar";
import { DashboardZeffyLoader } from "@/dashboard-design-system/ZeffyLoader/DashboardZeffyLoader";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- the bubble must mount inside the user and Amplitude providers, and this is the dashboard composition root
import { DonorAssistantWidgetGate } from "@/features/DonorAssistantWidget/DonorAssistantWidgetGate";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { G2ReviewPrompt } from "@/features/G2Review/G2ReviewPrompt/G2ReviewPrompt";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- the bubble must mount inside the user and Amplitude providers, and this is the dashboard composition root
import { OrganizationAssistantWidgetGate } from "@/features/OrganizationAssistant/OrganizationAssistantWidgetGate";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { RedirectOnUnsupportedDashboardBrowser } from "@/features/UnsupportedBrowser/RedirectOnUnsupportedDashboardBrowser";

import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/css/froala_style.min.css";
import "froala-editor/css/plugins.pkgd.min.css";

import "react-phone-input-2/lib/material.css";
import "react-phone-input-2/lib/style.css";

export const AppDashboardProvider = ({
  children,
  formPermissions,
  generalPermissions,
  disableLoading = false,
  enabledSSR = true,
  forcedIsoLocale,
}: {
  children: React.ReactNode;
  formPermissions?: Nullable<UserFormPermissions[]>;
  generalPermissions?: Nullable<UserGeneralPermissions[]>;
  disableLoading?: boolean;
  enabledSSR?: boolean;
  forcedIsoLocale?: IsoLocale;
}) => {
  useLogParamsEvent();
  const router = useRouter();
  useDisableSafariBackForwardCache();

  const [key, setKey] = useState(0);
  useEffect(() => {
    if (!enabledSSR) {
      setKey(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (key === 0 && !enabledSSR) {
    return <DashboardZeffyLoader />;
  }

  return (
    <>
      <PageLoadingProvider isLoading={router.isFallback} disableLoading={disableLoading}>
        <CurrentUserProvider>
          <CssBaseline />
          <ClarityProviderWrapper>
            <AmplitudeProvider>
              <OrganizationEngagementFlagsProvider>
                <StripeAccountProvider>
                  <LocaleProvider forcedIsoLocale={forcedIsoLocale}>
                    <ZeffySnackbarProvider>
                      <TwoFactorProvider>
                        <StrongAuthProvider>
                          <RedirectOnUnsupportedDashboardBrowser>
                            <CssBaseline />
                            <DevContextCopy formId={null} />
                            <OrganizationCategoryPopup />
                            <Mandatory2FASetupModal />
                            <G2ReviewPrompt />
                            <DonorAssistantWidgetGate />
                            <OrganizationAssistantWidgetGate />
                            <EmailEditorProvider>
                              <EmailTypeChoiceProvider>
                                <PermissionRoute
                                  formPermissions={formPermissions}
                                  generalPermissions={generalPermissions}
                                >
                                  {children}
                                </PermissionRoute>
                              </EmailTypeChoiceProvider>
                            </EmailEditorProvider>
                          </RedirectOnUnsupportedDashboardBrowser>
                        </StrongAuthProvider>
                      </TwoFactorProvider>
                    </ZeffySnackbarProvider>
                  </LocaleProvider>
                </StripeAccountProvider>
              </OrganizationEngagementFlagsProvider>
            </AmplitudeProvider>
          </ClarityProviderWrapper>
        </CurrentUserProvider>
      </PageLoadingProvider>
    </>
  );
};

const PermissionRoute = ({
  children,
  formPermissions,
  generalPermissions,
}: {
  children: React.ReactNode;
  formPermissions?: Emptyable<UserFormPermissions[]>;
  generalPermissions?: Emptyable<UserGeneralPermissions[]>;
}) => {
  const router = useRouter();
  useDisableSafariBackForwardCache();

  const isDonorPage = isDonorRoute(router.pathname);
  const isOrganizationPage = isOrganizationRoute(router.pathname);
  const isAdminPage = isAdminRoute(router.pathname);
  const isPublicPage = !isDonorPage && !isOrganizationPage && !isAdminPage;

  if (isDonorPage) {
    return <DonorRoute>{children}</DonorRoute>;
  }
  if (isOrganizationPage || isAdminPage) {
    return (
      <OrganizationRoute formPermissions={formPermissions} generalPermissions={generalPermissions}>
        {children}
      </OrganizationRoute>
    );
  }
  if (isPublicPage) {
    return <PublicRoute>{children}</PublicRoute>;
  }

  return null;
};
